import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  API_KEY_MAP,
  HEROKU_ID_KEY,
  AccountType,
  ContactType,
  MembershipType,
  NEW_MEMBER_CONTACT_ACTION,
} from 'types'
import { pure } from 'recompose'
import { useHistory } from 'react-router-dom'
import { SubmissionError } from 'redux-form'
import useAuth from 'components/auth/useAuth'
import { MemberContactInfoForm } from '../forms'
import {
  formValuesWithMappedKeys,
  apiValuesWithMappedKeys,
  emailsMatch,
  getNewMemberContactAction,
  resolveEditJoinSignInSnapshots,
  resolvePostLoginResumeAttachTarget,
  stripNoneTrailerCareType,
  useConfirm,
  handleSubmitFailWithFlashMessage,
  normalizePhoneNumbers,
} from 'utils'
import {
  PATH,
  makeMemberApplicationApplyPath,
  makeMembershipsPath,
  makePath,
} from 'config'
import * as LS from 'local-storage'

const propTypes = {
  initialAccount: AccountType.isRequired,
  initialContact: ContactType.isRequired,
  applicationContactId: PropTypes.string.isRequired,
  applicationMembershipId: PropTypes.string.isRequired,
  autoCompleteAddress: PropTypes.func.isRequired,
  // Guest join edit only: same primarycontactexists + modal flow as a first
  // Contact submit. Omitted for renewal / authenticated edit.
  clearShoppingCart: PropTypes.func,
  disableEmail: PropTypes.bool,
  fetchPlaceDetails: PropTypes.func.isRequired,
  lookupExistingContact: PropTypes.func,
  prefillAccount: AccountType,
  prefillContact: ContactType,
  prefillMembership: MembershipType,
  selectedTrailerCareType: PropTypes.string,
  shoppingCartMembershipType: PropTypes.string,
  successPath: PropTypes.string.isRequired,
  updateMembershipApplicationAccount: PropTypes.func.isRequired,
}

function EditMemberContactInfo({
  initialAccount,
  initialContact,
  applicationContactId,
  applicationMembershipId,
  autoCompleteAddress,
  clearShoppingCart = null,
  disableEmail = false,
  fetchPlaceDetails,
  lookupExistingContact = null,
  prefillAccount = null,
  prefillContact = null,
  prefillMembership = null,
  selectedTrailerCareType = null,
  shoppingCartMembershipType = null,
  successPath,
  updateMembershipApplicationAccount,
}) {
  const history = useHistory()
  const { login, user } = useAuth()
  const { isConfirmed: confirmModal } = useConfirm()
  const applicationAccountId = initialAccount[HEROKU_ID_KEY]

  // disableEmail is set after Auth0 on the apply Contact step (locks email
  // and enables resume). Renewal omits it so we don't overwrite the SF
  // email. A leftover join Sign-in form pair is still cleared on mount.
  const [{ savedFormValues, resumedExistingContact }] = useState(() => {
    const snapshots = resolveEditJoinSignInSnapshots({
      disableEmail,
      savedFormValues: LS.getNewMemberContactFormValues(),
      resumedExistingContact: LS.getExistingContactInfo() ?? null,
      userEmail: user?.email,
    })
    if (snapshots.clearSnapshots) {
      LS.clearNewMemberContactFormValues()
      LS.clearExistingContactInfo()
    }
    return {
      savedFormValues: snapshots.savedFormValues,
      resumedExistingContact: snapshots.resumedExistingContact,
    }
  })

  const cartAccountValues = formValuesWithMappedKeys(
    initialAccount,
    API_KEY_MAP.ACCOUNT_KEY_MAP
  )
  const cartContactValues = formValuesWithMappedKeys(
    initialContact,
    API_KEY_MAP.CONTACT_KEY_MAP
  )

  return (
    <MemberContactInfoForm
      autoCompleteAddress={autoCompleteAddress}
      disableEmail={disableEmail}
      fetchPlaceDetails={fetchPlaceDetails}
      initialValues={{
        account: {
          ...cartAccountValues,
          ...savedFormValues?.account,
        },
        contact: normalizePhoneNumbers({
          ...cartContactValues,
          ...savedFormValues?.contact,
          // Locked join email must match Auth0. Renewal keeps SF contact email.
          ...(disableEmail &&
            user?.email && {
              email: user.email,
            }),
        }),
      }}
      onSubmit={async (formValues) => {
        const accountApiValues = apiValuesWithMappedKeys(
          formValues.account,
          API_KEY_MAP.ACCOUNT_KEY_MAP
        )
        const contactApiValues = apiValuesWithMappedKeys(
          formValues.contact,
          API_KEY_MAP.CONTACT_KEY_MAP
        )

        const membership = {
          shopping_cart_membership_type__c: shoppingCartMembershipType,
          shopping_cart_trailer_care_type__c: stripNoneTrailerCareType(
            selectedTrailerCareType
          ),
        }

        const patchOwnApplicationAccount = () =>
          updateMembershipApplicationAccount({
            account: {
              [HEROKU_ID_KEY]: applicationAccountId,
              ...accountApiValues,
            },
            contact: {
              [HEROKU_ID_KEY]: applicationContactId,
              ...contactApiValues,
            },
            membership: {
              [HEROKU_ID_KEY]: applicationMembershipId,
            },
          })

        // Post-login resume: attach this application to the matched contact
        // instead of PATCH-ing the guest cart that still sits in redux.
        // Email-only Auth0 matches withhold lookup IDs — fall back to
        // dashboard prefill loaded after login. Only trust pre-login lookup
        // IDs when Auth0 is the same email we looked up (typed A, signed in
        // as B must not UPDATE A's records).
        // Both Sign-in snapshots must still be present. SIGN_IN writes them
        // together; leftover lookup-only or form-only is stale. After that
        // first save we clear LS, so a later visit just PATCHes this cart.
        const resumeAttachTarget = resolvePostLoginResumeAttachTarget({
          disableEmail,
          savedFormValues,
          resumedExistingContact,
          userEmail: user?.email,
          cartContactEmail: initialContact.email,
          prefillAccount,
          prefillContact,
          prefillMembership,
        })

        if (resumeAttachTarget) {
          const { accountId, contactId, membershipId } = resumeAttachTarget

          if (accountId && contactId) {
            await updateMembershipApplicationAccount({
              account: {
                [HEROKU_ID_KEY]: accountId,
                ...accountApiValues,
              },
              contact: {
                [HEROKU_ID_KEY]: contactId,
                ...contactApiValues,
              },
              membership: {
                ...(membershipId && {
                  [HEROKU_ID_KEY]: membershipId,
                }),
                ...membership,
              },
            })
            LS.clearNewMemberContactFormValues()
            LS.clearExistingContactInfo()
            return history.push(successPath)
          }
          // Resume without resolved IDs — don't PATCH the guest cart.
          throw new SubmissionError({
            _error:
              'Unable to load your account. Please try again in a moment.',
          })
        }

        // Renewal (no lookup) or authenticated join whose cart already
        // belongs to this user: PATCH the cart already in redux. A leftover
        // cart for someone else is handled above (prefill or throw).
        if (!lookupExistingContact) {
          await patchOwnApplicationAccount()
          return history.push(successPath)
        }

        // Same email as this cart: allow phone/name/address edits without
        // re-running duplicate-email lookup. A phone-only change would look
        // like an email-only mismatch (IDs withheld) and falsely block.
        if (emailsMatch(formValues.contact.email, initialContact.email)) {
          await patchOwnApplicationAccount()
          return history.push(successPath)
        }

        const { email, mobilePhone } = formValues.contact
        const existingContact = await lookupExistingContact({
          email,
          phone_number: mobilePhone,
        })

        // Lookup matched this cart's contact — continue editing it without
        // re-showing first-submit modals. Account id alone is not enough:
        // another contact on the same account must still go through the
        // duplicate-email flow.
        const isOwnApplicationContact =
          !!existingContact?.contact_hc_ext_id &&
          existingContact.contact_hc_ext_id === applicationContactId

        if (isOwnApplicationContact) {
          await patchOwnApplicationAccount()
          return history.push(successPath)
        }

        // Same modals / outcomes as a brand-new Contact submit for this shape.
        const action = await getNewMemberContactAction({
          existingContact,
          skipModals: false,
          confirmModal,
        })

        switch (action) {
          case NEW_MEMBER_CONTACT_ACTION.CANCEL:
            return

          case NEW_MEMBER_CONTACT_ACTION.RENEW:
            login({
              appState: {
                returnTo: makeMembershipsPath(
                  PATH.SELECT_YOUR_MEMBERSHIP,
                  PATH.RENEW_MEMBERSHIP
                ),
              },
            })
            return

          case NEW_MEMBER_CONTACT_ACTION.CREATE_LOGIN:
            login({
              appState: { returnTo: PATH.MEMBER_PORTAL },
            })
            return

          case NEW_MEMBER_CONTACT_ACTION.SIGN_IN:
            LS.setNewMemberContactFormValues(formValues)
            if (existingContact) {
              LS.setExistingContactInfo(existingContact)
            }
            login({
              appState: {
                returnTo: makeMemberApplicationApplyPath(PATH.CONTACT),
              },
            })
            return

          case NEW_MEMBER_CONTACT_ACTION.EXIT_TO_MARKETING:
            if (clearShoppingCart) clearShoppingCart()
            window.location.replace(makePath(PATH.SEATOW))
            return

          case NEW_MEMBER_CONTACT_ACTION.UPDATE:
            LS.setExistingContactInfo(existingContact)
            await updateMembershipApplicationAccount({
              account: {
                [HEROKU_ID_KEY]: existingContact.account_hc_ext_id,
                ...accountApiValues,
              },
              contact: {
                [HEROKU_ID_KEY]: existingContact.contact_hc_ext_id,
                ...contactApiValues,
              },
              membership: {
                ...(existingContact.membership_hc_ext_id && {
                  [HEROKU_ID_KEY]: existingContact.membership_hc_ext_id,
                }),
                ...membership,
              },
            })
            return history.push(successPath)

          case NEW_MEMBER_CONTACT_ACTION.CREATE:
            // Email is free, but this cart already exists — PATCH instead of
            // POSTing a second Account/Contact.
            await patchOwnApplicationAccount()
            return history.push(successPath)

          default:
            throw new SubmissionError({
              _error: 'Something went wrong. Please try again.',
            })
        }
      }}
      onSubmitFail={handleSubmitFailWithFlashMessage}
    />
  )
}

EditMemberContactInfo.propTypes = propTypes

export default pure(EditMemberContactInfo)
